import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { BrandLogo } from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { apiError, redirectToCheckout } from "@/lib/api";
import { HERO_BG } from "@/lib/brand";
import { GoogleAuthButton, OrDivider } from "@/components/GoogleAuthButton";
import { AccountingConnectButtons } from "@/components/ConnectAccounting";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

const PLAN_PRICE = { growth: 29, scale: 79 };
const planLabel = (plan, interval) => {
  if (plan === "free") return "Free";
  const name = plan === "scale" ? "Scale" : "Growth";
  const m = PLAN_PRICE[plan] || 29;
  return interval === "annual"
    ? `${name} — £${m * 10}/yr, billed annually (2 months free)`
    : `${name} — £${m}/mo, billed monthly`;
};

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const plan = params.get("plan") || "free";
  const interval = params.get("interval") === "annual" ? "annual" : "monthly";
  const [form, setForm] = useState({ name: "", email: "", company: "", trade: "", region: "", password: "" });
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const t = q.get("trade"); const r = q.get("region");
    if (t || r) setForm((f) => ({ ...f, ...(t ? { trade: t } : {}), ...(r ? { region: r } : {}) }));
  }, []);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    track("started_signup", { plan });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const connectXeroAfterSignup = () => {
    sessionStorage.setItem("tradeoff_connect_xero", "1");
    track("connect_intent", { provider: "xero", where: "signup" });
    toast.success("Great — create your account below and we'll connect Xero straight after.");
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogle = () => {
    track("started_signup", { plan, method: "google" });
    localStorage.setItem("bs_google_plan", plan);
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const startCheckout = async (planKey) => {
    track("checkout_started", { plan: planKey, interval });
    const { data } = await api.post("/billing/checkout", { plan: planKey, interval, origin_url: window.location.origin });
    redirectToCheckout(data.url);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, plan, origin_url: window.location.origin });
      track("sign_up", { plan, method: "email" });
      const wantsXero = sessionStorage.getItem("tradeoff_connect_xero") === "1";
      sessionStorage.removeItem("tradeoff_connect_xero");
      if (plan === "growth" || plan === "scale") {
        await startCheckout(plan);
      } else {
        navigate(wantsXero ? "/app/settings?connect=xero" : "/app");
      }
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="mb-8"><BrandLogo /></div>
        <div className="max-w-sm w-full">
          <h1 className="font-display text-3xl font-bold text-slate-900">Create your workspace</h1>
          <p className="text-slate-500 mt-2">
            Selected plan: <span className="font-semibold text-blue-600" data-testid="register-selected-plan">{planLabel(plan, interval)}</span>
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            <GoogleAuthButton label="Sign up with Google" onClick={startGoogle} disabled={loading} />
            <OrDivider />
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="register-error">{error}</div>}
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" value={form.name} onChange={set("name")} required data-testid="register-name" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="company">Company (optional)</Label>
              <Input id="company" value={form.company} onChange={set("company")} data-testid="register-company" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="trade">Your main trade (optional)</Label>
              <select id="trade" value={form.trade} onChange={set("trade")} data-testid="register-trade" className="mt-1 h-10 w-full border border-slate-300 rounded-md text-sm px-3 bg-white">
                <option value="">General / not sure</option>
                <option value="electrical">Electrical</option>
                <option value="plumbing_heating">Plumbing &amp; heating</option>
                <option value="groundworks">Groundworks &amp; drainage</option>
                <option value="brick_block">Brick &amp; block</option>
                <option value="carpentry">Carpentry &amp; joinery</option>
                <option value="plastering_drylining">Plastering &amp; drylining</option>
                <option value="painting_decorating">Painting &amp; decorating</option>
                <option value="flooring_tiling">Flooring &amp; tiling</option>
              </select>
              <p className="mt-1 text-xs text-slate-400">We'll load a matching starter rate library so you can price jobs from day one.</p>
            </div>
            <div>
              <Label htmlFor="region">Your region (optional)</Label>
              <select id="region" value={form.region} onChange={set("region")} data-testid="register-region" className="mt-1 h-10 w-full border border-slate-300 rounded-md text-sm px-3 bg-white">
                <option value="">UK national</option>
                <option value="london">London</option>
                <option value="south_east">South East</option>
                <option value="midlands">Midlands</option>
                <option value="north">North</option>
                <option value="scotland">Scotland</option>
                <option value="wales">Wales</option>
              </select>
              <p className="mt-1 text-xs text-slate-400">Sets your local pricing so rate benchmarks match your area.</p>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="register-email" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="register-password" className="mt-1" />
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-blue-600 hover:bg-blue-700 h-11">
              {loading ? "Creating…" : plan === "free" ? "Create free account" : "Continue to payment"}
            </Button>
            {plan !== "free" && (
              <p className="text-xs text-slate-400 text-center" data-testid="register-billing-note">
                {interval === "annual"
                  ? `You'll be billed £${(plan === "scale" ? 79 : 29) * 10} today for 12 months (2 months free). Cancel anytime.`
                  : `You'll be billed £${plan === "scale" ? "79" : "29"} today and every 30 days. Cancel anytime.`}
              </p>
            )}
          </form>
          <p className="mt-6 text-sm text-slate-500">
            Already have an account? <Link to="/login" className="text-blue-600 font-medium">Log in</Link>
          </p>
          <div className="mt-6 pt-6 border-t border-slate-100" data-testid="signup-connect-accounting">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2.5">Connect your accounting (optional)</p>
            <AccountingConnectButtons onConnectXero={connectXeroAfterSignup} />
            <p className="text-xs text-slate-400 mt-2">You can also do this later in Settings.</p>
          </div>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(22,48,92,0.85), rgba(37,99,235,0.55))" }} />
        <div className="relative h-full flex items-end p-12">
          <p className="text-white font-display text-2xl font-semibold max-w-sm">Every feature on every plan. Upgrade only for more active jobs.</p>
        </div>
      </div>
    </div>
  );
};

export default Register;
